import { useNavigate } from 'react-router-dom';
import type { Property } from '@/services/properties';

type Props = {
  title?: string;
  linkLabel?: string;
  linkTo?: string;
  properties: Property[];
  sectionId?: string;
  alternate?: boolean;
};

export default function FeaturedProperties({
  title = 'IMÓVEIS EM DESTAQUE',
  linkLabel = 'VER TODOS OS IMÓVEIS',
  linkTo = '/imoveis',
  properties,
  sectionId = 'imoveis',
  alternate = false,
}: Props) {
  const navigate = useNavigate();

  return (
    <section id={sectionId} className={`px-4 py-12 md:px-8 md:py-16 ${alternate ? 'bg-slate-50' : 'bg-background-50'}`}>
      <div>
        <div className="mb-8 flex items-center justify-between gap-4">
          <h2 className="font-sans text-xl font-bold tracking-wide text-secondary-900 md:text-2xl">{title}</h2>
          <button onClick={() => navigate(linkTo)} className="flex cursor-pointer items-center gap-1 whitespace-nowrap border-none bg-transparent text-sm font-semibold text-primary-500 transition-colors hover:text-primary-600">
            {linkLabel} <i className="ri-arrow-right-line" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {properties.map((property) => (
            <article key={property.id} onClick={() => navigate(`/imovel/${property.id}`)} className="group cursor-pointer overflow-hidden rounded-lg border border-background-200/70 bg-white transition-all hover:-translate-y-1 hover:shadow-lg">
              <div className="relative h-48 overflow-hidden bg-background-200">
                {property.image ? (
                  <img src={property.image} alt={property.title} className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-background-400">
                    <i className="ri-image-line text-3xl"></i>
                  </div>
                )}
                <span className={`absolute left-3 top-3 rounded px-2.5 py-1 text-[10px] font-bold tracking-wider text-white ${property.tag === 'VENDA' ? 'bg-primary-500' : 'bg-secondary-500'}`}>{property.tag}</span>
              </div>

              <div className="p-4">
                <p className="mb-1 text-[11px] text-foreground-600">{property.location}</p>
                <h3 className="mb-3 font-sans text-sm font-bold text-secondary-900">{property.title}</h3>
                <div className="mb-3 flex items-center gap-3 text-foreground-600">
                  {property.bedrooms > 0 && <span className="flex items-center gap-1 text-xs"><i className="ri-hotel-bed-line" />{property.bedrooms}</span>}
                  {property.bathrooms > 0 && <span className="flex items-center gap-1 text-xs"><i className="ri-showers-line" />{property.bathrooms}</span>}
                  {property.parking > 0 && <span className="flex items-center gap-1 text-xs"><i className="ri-car-line" />{property.parking}</span>}
                  <span className="flex items-center gap-1 text-xs"><i className="ri-shape-line" />{property.area}m²</span>
                </div>
                <p className="font-sans text-base font-extrabold text-primary-600">{property.price}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
